<template>
  <v-container>
    <v-empty-state
      v-if="loaded && !videos.length"
      icon="mdi-video-off-outline"
      size="64"
      title="Aucune vidéo pour cette section"
    ></v-empty-state>
    <v-row v-else-if="videos.length">
      <!-- Main player: one video at a time, picked from the list -->
      <v-col ref="player" cols="12" :md="videos.length > 1 ? 8 : 12">
        <video-embed :key="currentVideo.link" :src="currentVideo.link" class="video-player"></video-embed>
        <div class="d-flex align-start flex-wrap ga-2 mt-3">
          <p v-if="currentVideo.description" class="text-body-large mb-0 flex-grow-1">{{ currentVideo.description }}</p>
          <!-- Some videos are blocked by the provider inside the embedded player but still play on its site -->
          <v-btn
            :href="currentVideo.link"
            target="_blank"
            rel="noopener"
            variant="text"
            size="small"
            color="primary"
            append-icon="mdi-open-in-new"
          >
            Ouvrir sur {{ currentVideo.provider }}
          </v-btn>
        </div>
      </v-col>
      <v-col v-if="videos.length > 1" cols="12" md="4">
        <div class="text-label-large text-medium-emphasis mb-2">{{ videos.length }} vidéos</div>
        <v-list density="compact" class="py-0 video-list">
          <v-list-item
            v-for="(video, index) in videos"
            :key="`video-${index}`"
            :active="index === currentIndex"
            color="primary"
            rounded="lg"
            class="mb-1 px-2"
            @click="selectVideo(index)"
          >
            <template #prepend>
              <div class="video-thumb mr-3">
                <img v-if="video.thumbnailUrl" :src="video.thumbnailUrl" alt="" loading="lazy" />
                <v-icon v-else color="grey-lighten-1">mdi-video-outline</v-icon>
                <v-icon v-if="index === currentIndex" class="video-thumb__playing" color="white" size="small">
                  mdi-play
                </v-icon>
              </div>
            </template>
            <v-list-item-title class="text-body-medium video-title">
              {{ video.description || `Vidéo ${index + 1}` }}
            </v-list-item-title>
            <v-list-item-subtitle class="text-label-small">{{ video.provider }}</v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import SectionsService from "@/store/modules/sections";
  import { parseVideoLink } from "@/utils/videos";

  export default {
    props: {
      section: {
        type: Object,
        default: function () {
          return {};
        },
      },
    },
    data() {
      return {
        sectionVideos: [],
        currentIndex: 0,
        loaded: false,
      };
    },
    computed: {
      // Only links the player can embed
      videos() {
        return this.sectionVideos
          .map((video) => ({ ...video, ...parseVideoLink(video.link) }))
          .filter((video) => video.embedUrl);
      },
      currentVideo() {
        return this.videos[this.currentIndex] || this.videos[0];
      },
    },
    async mounted() {
      if (this.section.video_info) {
        await this.getSectionVideos(this.section.id);
      }
      this.loaded = true;
    },
    methods: {
      async getSectionVideos(id) {
        const sectionVideosDocs = await SectionsService.actions.findSectionVideos(id);
        if (sectionVideosDocs) {
          const sectionVideoLinks = sectionVideosDocs;
          this.sectionVideos = JSON.parse(sectionVideoLinks.links);
        }
      },
      selectVideo(index) {
        this.currentIndex = index;
        // On small screens the list is below the player: bring the player back into view
        if (this.$vuetify.display.smAndDown && this.$refs.player) {
          this.$refs.player.$el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .video-player {
    border-radius: 8px;
    overflow: hidden;
    background: #000;
  }
  .video-list {
    max-height: 60vh;
    overflow-y: auto;
  }
  .video-thumb {
    position: relative;
    flex: 0 0 auto;
    width: 112px;
    aspect-ratio: 16 / 9;
    border-radius: 6px;
    overflow: hidden;
    background: rgb(var(--v-theme-surface-variant));
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  .video-thumb__playing {
    position: absolute;
    inset: 0;
    margin: auto;
    background: rgba(0, 0, 0, 0.55);
    border-radius: 50%;
    padding: 14px;
  }
  .video-title {
    white-space: normal;
    line-height: 1.3;
  }
</style>
